import React from 'react';
import { IAddMembersGroup } from '../../types/customGroupingTypes';
import {
    GroupCSRLevels,
    PlAN_TYPES,
} from '../../../../constants/individualPortal/customGrouping/customGrouping.constants';
import MemberCard, { IMemberCardConfig } from '../common/MemberCard';
import { formatCurrency } from '../../../../utils/tools/formatCurrenyToInteger';
import useCustomGrouping from '../../hooks/useCustomGrouping';
import { useAppDispatch, useAppSelector, useModal } from '../../../../hooks';

interface IGroupedMembersWrapperProps {
    group: IAddMembersGroup;
    planType: PlAN_TYPES;
}

export default function GroupedMembersCard(props: IGroupedMembersWrapperProps) {
    const { group, planType } = props;
    const dispatch = useAppDispatch();
    const { addToExistingGroup, getMaxAptc, getMaxStateSubsidy, getSelectedCount } = useCustomGrouping({ dispatch });
    const { openModal } = useModal();
    const customGroupingState = useAppSelector(state => state.individualPortal.customGrouping);
    const { caseNumber } = customGroupingState;

    function getPlanDetailsFooter() {
        const footers: {
            footer: string;
            footerValues?: Record<string, any>;
        }[] = [];

        if (group.AddedInEnrolledGroup.groupCsr !== GroupCSRLevels.CS1)
            footers.push({
                footer: 'customGrouping.csrMsg',
            });

        if (group.AddedInEnrolledGroup.maxAptc > 0) {
            const maxAPTC = getMaxAptc(group.AddedInEnrolledGroup, planType);
            footers.push({
                footer: 'customGrouping.totalAPTC',
                footerValues: { amount: formatCurrency(maxAPTC) },
            });
        }

        if (group.AddedInEnrolledGroup.maxStateSubsidy > 0 && group.AddedInEnrolledGroup.groupType !== PlAN_TYPES.DENTAL) {
            const maxStateSubsidy = getMaxStateSubsidy(group.AddedInEnrolledGroup);
            footers.push({
                footer: 'customGrouping.totalStateSubsidy',
                footerValues: { amount: formatCurrency(maxStateSubsidy) },
            });
        }

        return footers;
    }

    const primaryCTA = {
        label: group.AddedInEnrolledGroup.isRenewal
            ? 'customGrouping.renewPlan'
            : 'customGrouping.addToPlan',
        labelValues: { members: group.AddedInEnrolledGroup.selectedMembersCount },
        onClick: ({ secondaryMembers }) => {
            const selectedGroup = {
                ...group.AddedInEnrolledGroup,
                memberList: [
                    ...primaryMembers.map(member => ({...member.data})),
                    ...secondaryMembers
                    .filter(member => member.config.isChecked)
                    .map(member => ({
                        ...member.data,
                        isChecked: true,
                    })),
                ],
            };
            addToExistingGroup(
                {
                    typeOfPlan: planType,
                    groupToChange: selectedGroup,
                    isChangePlan: false,
                    enablePopupForEndOfPHE: false,
                    caseNumber,
                },
                { openModal },
            );
        },
        isDisabled: ({ secondaryMembersState }) => !getSelectedCount(secondaryMembersState),
    };

    const planDetails = {
        footers: getPlanDetailsFooter(),
        planName: group.AddedInEnrolledGroup.planName,
        issuerName: group.AddedInEnrolledGroup.issuerName,
        netPremium:
            group.AddedInEnrolledGroup.netPremium
                ? 'customGrouping.netPremium'
                : '',
        perimoumAmount: formatCurrency(group.AddedInEnrolledGroup.netPremium, false),
    };

    const primaryMembers: IMemberCardConfig[] = [];
    const secondaryMembers: IMemberCardConfig[] = [];

    group.AddedInEnrolledGroup.memberList.forEach(member => {
        if (
            (planType === PlAN_TYPES.HEALTH && member.healthEnrollmentId) ||
            (planType === PlAN_TYPES.DENTAL && member.dentalEnrollmentId)
        ) {
            primaryMembers.push({
                data: member,
                config: {
                    isCheckBoxVisible: group.AddedInEnrolledGroup.isRenewal,
                },
            });
        } else {
            secondaryMembers.push({
                data: member,
                config: {
                    isCheckBoxVisible: true,
                    isChecked: member.isChecked,
                },
            });
        }
    });

    return (
        <>
            <MemberCard
                id={String(group.AddedInEnrolledGroup.id)}
                primaryMembers={primaryMembers}
                secondaryMembers={secondaryMembers}
                planDetails={planDetails}
                primaryCTA={primaryCTA}
            />
        </>
    );
}
